import type { ReactNode } from 'react'
import { Card, CardContent } from '@saas-pages/shared/ui'
import { cn } from '@saas-pages/shared/ui'
import { format, useT } from '@/shared/i18n/I18nProvider'
import {
  usePdfPageThumbnails,
  type PdfPageThumbnail,
} from '@/features/pdf-page-preview/model/lib'

type MarkerTone = 'primary' | 'destructive'

interface PdfPagePreviewProps {
  file: File | null
  totalPages?: number | null
  maxPages?: number
  pageNumbers?: readonly number[]
  selectedPages?: ReadonlySet<number>
  markerTone?: MarkerTone
  markerLabel?: string
  transformByPage?: ReadonlyMap<number, { rotate?: number }>
  renderOverlay?: (thumbnail: PdfPageThumbnail) => ReactNode
  showLimitNotice?: boolean
  title?: string
  description?: string
  className?: string
  onDocumentLoad?: (totalPages: number) => void
}

export function PdfPagePreview({
  file,
  totalPages,
  maxPages = 12,
  pageNumbers,
  selectedPages,
  markerTone = 'primary',
  markerLabel,
  transformByPage,
  renderOverlay,
  showLimitNotice = true,
  title,
  description,
  className,
  onDocumentLoad,
}: PdfPagePreviewProps) {
  const t = useT()
  const preview = usePdfPageThumbnails({
    file,
    pageNumbers,
    maxPages,
    onDocumentLoad,
  })
  const effectiveTotalPages = preview.totalPages ?? totalPages ?? null
  const limited = effectiveTotalPages !== null && effectiveTotalPages > maxPages
  const shownCount = preview.thumbnails.length

  if (!file) return null

  return (
    <Card className={cn('border-primary/15 bg-primary/5', className)}>
      <CardContent className="space-y-4 p-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">{title ?? t.pdfPreview.title}</h2>
            <p className="text-xs text-muted-foreground">
              {description ?? t.pdfPreview.description}
            </p>
          </div>
          {effectiveTotalPages !== null && (
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              {format(t.pdfPreview.totalPages, { pages: effectiveTotalPages })}
            </span>
          )}
        </div>

        {preview.status === 'error' ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
            {t.pdfPreview.failed}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {preview.thumbnails.map((thumbnail) => (
              <ThumbnailCard
                key={thumbnail.pageNumber}
                thumbnail={thumbnail}
                selected={selectedPages?.has(thumbnail.pageNumber) ?? false}
                markerTone={markerTone}
                markerLabel={markerLabel ?? t.pdfPreview.selected}
                transform={transformByPage?.get(thumbnail.pageNumber)}
                overlay={renderOverlay?.(thumbnail)}
              />
            ))}

            {preview.status === 'loading' && shownCount === 0 && (
              <>
                {Array.from({ length: Math.min(maxPages, effectiveTotalPages ?? 4) }, (_, index) => (
                  <div
                    key={index}
                    className="flex aspect-[3/4] animate-pulse items-center justify-center rounded-md border bg-background"
                  >
                    <span className="text-xs text-muted-foreground">{t.pdfPreview.loading}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        <div className="space-y-1 text-xs text-muted-foreground">
          {shownCount > 0 && effectiveTotalPages !== null && (
            <p>
              {format(t.pdfPreview.renderedPages, {
                shown: shownCount,
                total: effectiveTotalPages,
              })}
            </p>
          )}
          {limited && showLimitNotice && <p>{format(t.pdfPreview.limited, { limit: maxPages })}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

interface ThumbnailCardProps {
  thumbnail: PdfPageThumbnail
  selected: boolean
  markerTone: MarkerTone
  markerLabel: string
  transform?: { rotate?: number }
  overlay?: ReactNode
}

function ThumbnailCard({
  thumbnail,
  selected,
  markerTone,
  markerLabel,
  transform,
  overlay,
}: ThumbnailCardProps) {
  const t = useT()
  const rotate = transform?.rotate ?? 0

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-md border bg-background p-2 shadow-sm',
        selected && markerTone === 'primary' && 'border-primary ring-1 ring-primary',
        selected && markerTone === 'destructive' && 'border-destructive ring-1 ring-destructive',
      )}
    >
      <div className="relative flex aspect-[3/4] items-center justify-center overflow-hidden rounded border bg-white">
        <img
          src={thumbnail.dataUrl}
          alt={format(t.pdfPreview.pageAlt, { page: thumbnail.pageNumber })}
          className="max-h-full max-w-full object-contain transition-transform"
          style={{ transform: rotate ? `rotate(${rotate}deg)` : undefined }}
          loading="lazy"
        />
        {overlay}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
        <span className="font-medium text-foreground">
          {format(t.pdfPreview.pageLabel, { page: thumbnail.pageNumber })}
        </span>
        {selected && (
          <span
            className={cn(
              'rounded px-1.5 py-0.5 font-medium',
              markerTone === 'destructive'
                ? 'bg-destructive/10 text-destructive'
                : 'bg-primary/10 text-primary',
            )}
          >
            {markerLabel}
          </span>
        )}
      </div>
    </div>
  )
}
